import React, { forwardRef } from "react";

import TrainingCourseImg from "../../../assets/used-images/defaultImg.jpeg";
import IvoryPrint from "../../../assets/used-images/IvoryPrint.png";
import IvoryPrintSperator from "../../../assets/used-images/IvoryPrintSperator.png";
import CertificateOne from "../../../assets/used-images/certificateprint1.jpg";
import CertificateTwo from "../../../assets/used-images/certificateprint2.jpg";
import IvoryList from "../../../assets/used-images/IvoryPrintList.png";
import IvoryPrintEnd from "../../../assets/used-images/IvoryPrintEnd.jpg";
import IvoryPrintBeforeEnd from "../../../assets/used-images/IvoryPrintBeforeEnd.jpg";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../store/language-context";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const PDF = forwardRef(({ course, priceId, selectedPayment }, ref) => {
    const { t } = useTranslation();

    const { URL } = useLanguage();
    const { id } = useParams();

    const { data } = useQuery({
        queryKey: ['city-distributions', id, URL],
        queryFn: async () => {
            try {
                const response = await axios.get(`${URL}/courses/${id}?city_id=6`);
                return response?.data?.data
            } catch (error) {
                throw new Error(t('custom.err'));
            }
        },
    });

    const startDate = new Date(data?.distribution[0]?.start_date);
    const endDate = new Date(data?.distribution[0]?.end_date);

    const differenceInMs = endDate - startDate;
    const differenceInDays = differenceInMs / (1000 * 60 * 60 * 24) + 1;

    const retrunCleanDesc = (htmlString) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');

        const images = doc.querySelectorAll('img');
        images.forEach(img => img.remove());

        return doc.body.textContent || '';
    };
    const retrunCleanText = retrunCleanDesc(course?.description);

    return (
        <div ref={ref} dir="rtl" className="*:text-right">
            {/* course title & image */}
            <div className="px-[2em] relative *:text-right">
                {/* title */}
                <div className="w-[92dvw] py-[2em] relative z-[9] bg-white flex justify-center items-center">
                    <img src={IvoryPrint} alt="image-first-page" className="w-[50dvw]" />
                </div>
                <div>
                    <h1 className="text-[40pt] text-center mb-[3cm] mt-[2cm] font-bold text-primary-tw"
                        style={{ lineHeight: '1.2' }}>
                        {course?.title}
                    </h1>
                </div>

                {/* image */}
                <div>
                    <div className="bg-secondary-tw w-[92dvw] h-[40dvh] absolute z-[2] top-[35dvh] rotate-6"></div>
                    <div className="bg-primary-tw w-[92dvw] h-[40dvh] absolute z-[2] top-[35dvh] -rotate-6"></div>
                    <img src={course?.image || TrainingCourseImg} alt="ivory-course-image"
                        className="h-[40dvh] w-[92dvw] relative z-[10]" />
                </div>
            </div>

            {/* course details */}
            <div className="relative px-[2em] mt-[10em] content">
                {/* default image in every page header */}
                <img src={IvoryPrintSperator} alt="ivory-logo" className="print-image" />
                {/* نبذه عن البرنامج و الاهداف */}
                <div>
                    {/* التفاصيل */}
                    <div className="bg-primary-tw p-[2em] rounded-tl-[40px] rounded-br-[40px]">
                        <h1 className="text-white text-[25pt] mb-[0.7em] !text-right" dir="rtl">
                            {t('pdf.details')}
                        </h1>
                        <article className="text-white text-[18pt]" style={{ textAlign: "justify" }} dir="rtl">
                            {retrunCleanText}
                        </article>
                    </div>

                    {/* الاهداف */}
                    <div className="mb-[2em] mt-[8em] !px-6 same-page">
                        <h2 className="text-primary-tw text-[25pt] font-semibold !text-right">
                            {t('pdf.goals')}
                        </h2>
                        <p className="text-primary-tw text-[16pt] !text-right">
                            : سيكون المشارك في نهاية البرنامج التدريبي قادر على
                        </p>
                        <ul className="two-column px-[0.5cm]" dir="rtl">
                            {
                                course?.goals?.map((goal, i) => (
                                    <li key={goal?.id || i} className="flex items-start gap-2 !text-right !my-2">
                                        <span>
                                            <img src={IvoryList} alt="ivory-list" className="w-[1em] !mt-3" />
                                        </span>
                                        <span className="text-[14pt]">
                                            {goal?.title}
                                        </span>
                                    </li>
                                ))
                            }
                        </ul>
                    </div>

                </div>

                {/* الفئه و الجدارات */}
                <div className="content">
                    {/* الفئات المستهدفة */}
                    <div>
                        <h2 className="text-primary-tw text-[25pt] font-semibold !text-right">
                            {t('training-courses.target')}
                        </h2>
                        <ul className="two-column !px-[0.5cm]" dir="rtl">
                            {
                                course?.targets?.map((target, i) => (
                                    <li key={target?.id || i} className="flex items-start gap-2 !text-right !my-2">
                                        <span>
                                            <img src={IvoryList} alt="ivory-list" className="w-[1em] !mt-3" />
                                        </span>
                                        <span className="text-[14pt]">
                                            {target?.title}
                                        </span>
                                    </li>
                                ))
                            }
                        </ul>
                    </div>
                    {/* الجدارات */}
                    {
                        (course?.skills && course?.skills.length > 0) &&
                        <div className="mt-4">
                            <h2 className="text-primary-tw text-[25pt] font-semibold !text-right">
                                {t('training-courses.skills')}
                            </h2>
                            <ul className="!px-[0.5cm]" dir="rtl">
                                {
                                    course?.skills?.map((skill, i) => (
                                        <li key={skill?.id || i} className="flex items-center gap-2 !text-right !my-2">
                                            <span>
                                                <img src={IvoryList} alt="ivory-list" className="w-[0.6em]" />
                                            </span>
                                            <span className="text-[14pt]">
                                                {skill?.title}
                                            </span>
                                        </li>
                                    ))
                                }
                            </ul>
                        </div>
                    }
                </div>

                {/* محاور البرنامج */}
                <div className="content !mt-[80px]">
                    <h2 className="text-primary-tw text-[25pt] font-semibold text-center mb-[10px]">
                        {t('pdf.content')}
                    </h2>
                    <div className="two-column" dir="rtl">
                        {
                            course?.trainingProgram?.map((item) => (
                                <div key={item?.id} className="!text-right !my-1">
                                    <div className="flex items-center gap-[0.6em] mx-[2em]" dir="rtl">
                                        <span>
                                            <img src={IvoryList} alt="ivory-list" className="w-[1.5em] mb-[0.5em]" />
                                        </span>
                                        <h2 className="text-primary-tw text-[14pt] font-semibold !text-right mb-[0.3em]">
                                            {item?.title}
                                        </h2>
                                    </div>
                                    <ul className="!text-right list-disc my-3" dir="rtl">
                                        {
                                            item?.countent?.map((dataOfItem, ix) => (
                                                <li key={ix} className="!!text-right mx-[3em] list-disc">
                                                    {dataOfItem?.title}
                                                </li>
                                            ))
                                        }
                                    </ul>
                                </div>
                            ))
                        }
                    </div>
                </div>

                {/* تفاصيل الانعقاد */}
                <div className="mb-[2em] mt-[5em] content">
                    <div className="flex items-center text-right gap-[0.6em]" dir="rtl">
                        <span dir="rtl">
                            <img src={IvoryList} alt="ivory-list" className="w-[1.5em] mb-[0.5em]" />
                        </span>
                        <span className="text-primary-tw text-[19pt] font-semibold text-right mb-[0.3em]" dir="rtl">
                            {t('pdf.details-2')}
                        </span>
                    </div>
                    <div>
                        <div className=" gap-[0.5em] !text-right my-[0.1cm]">
                            <span className="text-wrap text-[15pt] text-primary-tw ml-2">
                                مدة البرنامج التدريبي :
                            </span>
                            <span className="text-wrap text-[15pt]">
                                {differenceInDays || '5'} أيام بواقع {course?.count_hours} ساعة تدريبية
                            </span>
                        </div>
                        <div className=" gap-[0.5em] !text-right my-[0.1cm]">
                            <span className="text-nowrap text-[15pt] text-primary-tw ml-2">
                                {t('pdf.practical')} :
                            </span>
                            <span className="text-wrap text-[15pt]">
                                التطبيق العملي داخل القاعة التدريبية و ورش العمل بنسبة 70 بالمائة
                            </span>
                        </div>
                    </div>
                </div>

                {/* الشهادات */}
                <div>
                    <h2 className="text-primary-tw text-[25pt] font-semibold !text-right mt-[0.4em] mb-[0.5em]">
                        {t('training-courses.certificates')}
                    </h2>
                    <div className="py-[0.4cm] px-[1cm] mt-[0.5em] mx-[0.2em] border-[0.5px] border-[#d4ad89] rounded-[8px] flex justify-center items-center">
                        <img src={CertificateOne} alt="cert-one" className=" w-[98dvw] rounded-[12px]" />
                    </div>
                    <div className="py-[0.4cm] px-[1cm] mt-[0.5em] mx-[0.2em] border-[0.5px] border-[#d4ad89] rounded-[8px] flex justify-center items-center">
                        <img src={CertificateTwo} alt="cert-two" className=" w-[98dvw] rounded-[12px]" />
                    </div>
                    {
                        course?.certificate?.map((certificate, i) => (
                            <div className="py-[0.4cm] px-[1cm] mt-[0.5em] mx-[0.2em] border-[0.5px] border-[#d4ad89] rounded-[8px] flex justify-center items-center" key={i}>
                                <img src={certificate?.image} alt="cert-three" className=" w-[98dvw] rounded-[12px]" />
                            </div>
                        ))
                    }
                </div>


            </div>

            {/* before last page */}
            <div className="same-page bg-white">
                <img src={IvoryPrintBeforeEnd} alt="before-end-page" className="h-[100vh] w-[100vw] same-page" />
            </div>

            {/* last page */}
            <div className="same-page">
                <img src={IvoryPrintEnd} alt="end-page" className="h-[100vh] w-[100vw] same-page relative z-[5]" />
            </div>
        </div>
    )
});

export default PDF;